import React, { createRef, useEffect, useState } from "react";
import {
    Card,
    List,
    Avatar,
    Button,
    Space,
    Tag,
    Typography,
    Badge,
    Divider,
    Empty,
    Row,
    Col,
    Statistic,
    Modal,
    Spin
} from "antd";
import {
    ZoomInOutlined,
    CreditCardFilled,
    CheckCircleOutlined,
    ClockCircleOutlined,
    WalletOutlined,
    BankOutlined,
    MobileOutlined,
    DollarOutlined,
    FileTextOutlined,
    CalendarOutlined,
    StopOutlined,
    ExclamationCircleFilled,
    EditOutlined
} from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { frenchDate } from "../../../../variables/functions";
import { displayNumbers } from "../../../../variables/numbersFunctions";
import axiosInstance from "../../../../variables/api/axiosInstance";
import RecuCurrentFormat from "../../components/docs/RecuCurrentFormat";
import DTRFFormat from "../../components/docs/DTRFFormat";
import DTRFFormatProposition from "../../components/docs/DTRFFormatProposition";
import DTRFWPPFormat from "../../components/docs/DTRFWPPFormat";

const { confirm } = Modal;

const { Title, Text } = Typography;

const MouvementsContent = ({
    reglements,
    contrat,
    rightsService
}) => {

    const printRecu = createRef();
    const [openRecu, setOpenRecu] = useState(false);
    const [selectedReglement, setSelectedReglement] = useState(null);

    const handleRecu = useReactToPrint({
        content: () => printRecu.current,
    });

    const loadModal = (item) => {
        setSelectedReglement(item);
        console.log("item recu ", item);
        console.log("contrat recu ", contrat);
        setTimeout(() => {
            setOpenRecu(true);
        }, 500);
    }

    // Prévisualisation du CP (Conditions Particulières) tel qu'il était AVANT chaque mouvement.
    // Le règlement expose (via `contratCodeId`) la version du contrat CRÉÉE par l'avenant,
    // c.-à-d. l'état APRÈS/pendant le mouvement (souvent la proposition en cours). Pour afficher
    // l'ANCIEN contrat (état figé avant modification), on demande au backend la version
    // PRÉCÉDENTE de cette version. À défaut de référence, on retombe sur le contrat courant.
    const printCP = createRef();
    const [openCP, setOpenCP] = useState(false);
    const [selectedCPContrat, setSelectedCPContrat] = useState(null);
    const [loadingCP, setLoadingCP] = useState(false);

    const handleCP = useReactToPrint({
        content: () => printCP.current,
    });

    // Repli : dernière version VALIDÉE de la police — JAMAIS la proposition courante `contrat`
    // (qui peut être un renouvellement en cours). Utilisé quand la version du mouvement est
    // introuvable (FK règlement nul) ou que l'appel échoue.
    const chargerDerniereVersionValidee = () => {
        const police = contrat?.numeroPoliceP || contrat?.numeroPoliceM;
        if (!police) {
            toast.error("CP indisponible pour ce mouvement");
            setSelectedCPContrat(null);
            return Promise.resolve();
        }
        return axiosInstance
            .get(`/api/contrat/derniere-version-validee/${encodeURIComponent(police)}`)
            .then((res) => {
                if (res.data) {
                    setSelectedCPContrat(res.data);
                } else {
                    toast.error("CP indisponible pour ce mouvement");
                    setSelectedCPContrat(null);
                }
            })
            .catch((err) => {
                console.log(err);
                toast.error("CP indisponible pour ce mouvement");
                setSelectedCPContrat(null);
            });
    };

    const loadCP = (item) => {
        setSelectedCPContrat(null);
        setOpenCP(true);
        // `contratCodeId` = version (avenant) exacte du contrat liée à ce règlement,
        // exposée par le backend (le contrat lui-même est masqué via @JsonBackReference).
        const codeId = item?.contratCodeId || item?.contrat?.codeId;
        setLoadingCP(true);
        if (!codeId) {
            // Pas de référence de version : on affiche la dernière version validée (jamais la proposition).
            chargerDerniereVersionValidee().finally(() => setLoadingCP(false));
            return;
        }
        // /version-precedente/ remonte à la version PRÉCÉDENTE (état AVANT le mouvement) de la
        // version liée au règlement, même historisée (active_data = FALSE). Pour une Affaire
        // Nouvelle (sans prédécesseur), le backend renvoie la version elle-même — et jamais une
        // proposition (le backend remonte au dernier avenant VALIDÉ le cas échéant).
        axiosInstance.get(`/api/contrat/version-precedente/${codeId}`)
            .then((res) => {
                if (res.data) {
                    setSelectedCPContrat(res.data);
                    return null;
                }
                return chargerDerniereVersionValidee();
            })
            .catch((err) => {
                console.log(err);
                return chargerDerniereVersionValidee();
            })
            .finally(() => setLoadingCP(false));
    };

    // Sélection du bon format de CP (identique à l'onglet Contrat).
    const renderCPDocument = (c, ref) => {
        if (!c) return null;
        if (c?.statutContrat === "EN_PROPOSITION") {
            return <DTRFFormatProposition contrat={c} ref={ref} historique />;
        }
        if (c?.engins?.[0]?.categorie?.codeCategorie === "M" && c?.organime?.typeOrganisme === "POOL") {
            return <DTRFWPPFormat contrat={c} ref={ref} historique />;
        }
        // `historique` : CP d'un mouvement PASSÉ → afficher les montants STOCKÉS sur la
        // version, pas une re-tarification aux paramètres du jour (cf. DTRFFormat).
        return <DTRFFormat contrat={c} ref={ref} historique />;
    };

    // Annulation de l'avenant depuis sa ligne de règlement (uniquement si NON réglé).
    const confirmAnnulerAvenant = (item) => {
        // Avenant « règlement seul » (Duplicata AD) : ne modifie pas le contrat, il n'a créé
        // qu'une ligne de règlement → l'annulation supprime juste cette ligne (rien à contrepasser).
        const estReglementSeul = item?.avenant?.code === "AD";
        confirm({
            title: "Annuler cet avenant ?",
            icon: <ExclamationCircleFilled />,
            content: estReglementSeul
                ? "Cet avenant de duplicata sera annulé : sa ligne de règlement sera supprimée. "
                    + "Le contrat n'est pas modifié. Action historisée."
                : "L'avenant sera marqué ANNULÉ et sa ligne de règlement annulée. "
                    + "L'écriture d'émission sera contrepassée. Action sensible et historisée.",
            okText: "Oui, annuler l'avenant",
            okType: "danger",
            cancelText: "Retour",
            onOk() {
                return axiosInstance
                    .put(`/api/contrat/avenant/annuler-par-reglement/${item?.codeId}`)
                    .then(() => {
                        toast.success("Avenant annulé. Sa ligne de règlement a été annulée.");
                        window.location.reload();
                    })
                    .catch((err) => {
                        const msg = err?.response?.data?.message
                            || "Erreur lors de l'annulation de l'avenant";
                        toast.error(msg);
                    });
            },
        });
    };

    const primaryColor = "#1890ff";
    const secondaryColor = "#242166";
    const successColor = "#52c41a";
    const warningColor = "#faad14";
    // const dangerColor = "#ff4d4f";

    const cardStyle = {
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: `1px solid ${secondaryColor}15`,
        marginBottom: "16px"
    };

    const getPaymentIcon = (modePaiement) => {
        switch (modePaiement) {
            case "ESPECES":
                return <WalletOutlined style={{ fontSize: "20px", color: successColor }} />;
            case "ESPECE":
                return <WalletOutlined style={{ fontSize: "20px", color: successColor }} />;
            case "BANQUE":
                return <BankOutlined style={{ fontSize: "20px", color: primaryColor }} />;
            case "MOBILE_MONEY":
                return <MobileOutlined style={{ fontSize: "20px", color: warningColor }} />;
            case "RISTOURNE":
                return <DollarOutlined style={{ fontSize: "20px", color: secondaryColor }} />;
            default:
                return <FileTextOutlined style={{ fontSize: "20px", color: "#999" }} />;
        }
    };

    const getPaymentMethodLabel = (modePaiement) => {
        const labels = {
            "ESPECES": "Espèces",
            "ESPECE": "Espèces",
            "BANQUE": "Chèque Bancaire",
            "MOBILE_MONEY": "Mobile Money",
            "RISTOURNE": "Ristourne"
        };
        return labels[modePaiement] || "Non défini";
    };

    const getStatusTag = (item) => {
        // Avenant SANS prime (suspension, remise en vigueur, avenant d'ordre à 0…) : mouvement sans
        // règlement → statut neutre dédié (ni payé, ni à régler).
        if (item?.sansPrime) {
            return (
                <Tag
                    icon={<FileTextOutlined />}
                    color="default"
                    style={{ borderRadius: "6px", padding: "4px 12px", fontSize: "13px", fontWeight: 500 }}
                >
                    Sans prime
                </Tag>
            );
        }
        // Règlement annulé (avenant annulé) : statut prioritaire, ligne conservée mais marquée.
        if (item?.annule) {
            return (
                <Tag
                    icon={<StopOutlined />}
                    color="error"
                    style={{
                        borderRadius: "6px",
                        padding: "4px 12px",
                        fontSize: "13px",
                        fontWeight: 500
                    }}
                >
                    Annulé
                </Tag>
            );
        }
        // Encaissement PARTIEL : encaissé mais montantPaye < montant → tag distinct (or).
        if ((item.modePaiement || item.montantPaye) && estPartiel(item)) {
            return (
                <Tag
                    icon={<ClockCircleOutlined />}
                    color="gold"
                    style={{
                        borderRadius: "6px",
                        padding: "4px 12px",
                        fontSize: "13px",
                        fontWeight: 500
                    }}
                >
                    Partiel
                </Tag>
            );
        }
        if (item.modePaiement || item.montantPaye || item?.avenant?.code === "AN") {
            return (
                <Tag
                    icon={<CheckCircleOutlined />}
                    color="success"
                    style={{
                        borderRadius: "6px",
                        padding: "4px 12px",
                        fontSize: "13px",
                        fontWeight: 500
                    }}
                >
                    Payé
                </Tag>
            );
        }
        return (
            <Tag
                icon={<ClockCircleOutlined />}
                color="warning"
                style={{
                    borderRadius: "6px",
                    padding: "4px 12px",
                    fontSize: "13px",
                    fontWeight: 500
                }}
            >
                En attente
            </Tag>
        );
    };

    const getVersementTag = (versementCompagnie) => {
        if (versementCompagnie) {
            return (
                <Tag
                    color="success"
                    style={{
                        borderRadius: "6px",
                        padding: "4px 12px",
                        fontSize: "12px"
                    }}
                >
                    Prime Versée
                </Tag>
            );
        }
        return (
            <Tag
                color="error"
                style={{
                    borderRadius: "6px",
                    padding: "4px 12px",
                    fontSize: "12px"
                }}
            >
                Prime Non Versée
            </Tag>
        );
    };

    // Helpers encaissement PARTIEL : montant réellement versé (repli plein si montantPaye absent),
    // détection d'un partiel (montantPaye < montant) et reste à payer (plafonné à 0).
    const montantPayeEff = (r) => (r?.montantPaye != null ? r.montantPaye : (r?.montant || 0));
    const estPartiel = (r) => r?.montantPaye != null && r?.montant != null && r.montantPaye < r.montant;
    const resteAPayer = (r) => Math.max((r?.montant || 0) - montantPayeEff(r), 0);

    // Calculer les statistiques (les règlements ANNULÉS sont exclus des totaux payé / en attente)
    // Payé = montant RÉELLEMENT encaissé (montantPaye) → un encaissement partiel ne compte que le versé.
    const totalPaye = reglements?.filter(r => !r?.annule && (r.modePaiement || r.montantPaye)).reduce((acc, r) => acc + montantPayeEff(r), 0) || 0;
    // En attente = règlements non encaissés (montant plein) + reste à payer des encaissements partiels.
    // Les avenants SANS prime (mouvements synthétiques) n'entrent dans aucun total.
    const totalEnAttente = reglements?.filter(r => !r?.annule && !r?.sansPrime).reduce((acc, r) => {
        const encaisse = r.modePaiement || r.montantPaye || r?.avenant?.code === "AN";
        if (!encaisse) return r?.avenant?.code !== "AN" ? acc + (r.montant || 0) : acc;
        return acc + (estPartiel(r) ? resteAPayer(r) : 0);
    }, 0) || 0;
    const nombreMouvements = reglements?.length || 0;

    // Tri chronologique (du plus ancien au plus récent) pour un affichage type timeline.
    // Date de référence : createdAt, sinon dateReglement.
    const sortedReglements = [...(reglements || [])].sort((a, b) => {
        const da = new Date(a?.createdAt || a?.dateReglement || 0).getTime();
        const db = new Date(b?.createdAt || b?.dateReglement || 0).getTime();
        return da - db;
    });

    // ── Traçabilité : validation de la version liée au mouvement, encaisseur, auteur de la modification ──
    // Nom lisible d'un utilisateur : « Prénom Nom » (via personne), repli username.
    const nomUtilisateur = (u) => {
        if (!u) return null;
        const full = [u?.personne?.prenom, u?.personne?.nom].filter(Boolean).join(" ").trim();
        return full || u?.username || null;
    };
    // Versions (avenants) liées aux mouvements : codeId → { dateValidation, validateur }.
    const [versionsValidation, setVersionsValidation] = useState({});
    // Auteurs des modifications : codeId utilisateur (updatedBy) → nom lisible.
    const [modificateurs, setModificateurs] = useState({});

    useEffect(() => {
        const codes = [...new Set((reglements || []).map((r) => r?.contratCodeId).filter(Boolean))];
        const connues = contrat?.codeId ? { [contrat.codeId]: contrat } : {};
        setVersionsValidation(connues);
        codes.filter((c) => !connues[c]).forEach((c) => {
            axiosInstance.get(`/api/contrat/version/${encodeURIComponent(c)}`)
                .then(({ data }) => {
                    if (data) setVersionsValidation((prev) => ({ ...prev, [c]: data }));
                })
                .catch((err) => console.log(err));
        });
    }, [reglements, contrat]);

    useEffect(() => {
        const codes = [...new Set((reglements || []).map((r) => r?.updatedBy).filter(Boolean))];
        setModificateurs({});
        codes.forEach((c) => {
            axiosInstance.get(`/api/utilisateur/codeId/${encodeURIComponent(c)}`)
                .then(({ data }) => {
                    const nom = nomUtilisateur(data);
                    if (nom) setModificateurs((prev) => ({ ...prev, [c]: nom }));
                })
                .catch((err) => console.log(err));
        });
    }, [reglements]);

    return (
        <div style={{
            background: "white",
            padding: "32px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}>
            {/* En-tête avec statistiques */}
            <div style={{
                marginBottom: "32px",
                paddingBottom: "24px",
                borderBottom: `2px solid ${secondaryColor}20`
            }}>
                <Title level={3} style={{ margin: 0, color: secondaryColor, marginBottom: "24px" }}>
                    <FileTextOutlined style={{ marginRight: "12px" }} />
                    Historique des Mouvements
                </Title>

                <Row gutter={[24, 24]}>
                    <Col xs={24} sm={8}>
                        <Card style={{ ...cardStyle, border: `2px solid ${successColor}30` }}>
                            <Statistic
                                title={<Text style={{ color: "#666", fontSize: "14px" }}>Total Payé</Text>}
                                value={displayNumbers(totalPaye)}
                                suffix="FCFA"
                                valueStyle={{ color: successColor, fontSize: "24px", fontWeight: 600 }}
                                prefix={<CheckCircleOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card style={{ ...cardStyle, border: `2px solid ${warningColor}30` }}>
                            <Statistic
                                title={<Text style={{ color: "#666", fontSize: "14px" }}>En Attente</Text>}
                                value={displayNumbers(totalEnAttente)}
                                suffix="FCFA"
                                valueStyle={{ color: warningColor, fontSize: "24px", fontWeight: 600 }}
                                prefix={<ClockCircleOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card style={{ ...cardStyle, border: `2px solid ${primaryColor}30` }}>
                            <Statistic
                                title={<Text style={{ color: "#666", fontSize: "14px" }}>Nombre de Mouvements</Text>}
                                value={nombreMouvements}
                                valueStyle={{ color: primaryColor, fontSize: "24px", fontWeight: 600 }}
                                prefix={<FileTextOutlined />}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Liste des mouvements */}
            {sortedReglements && sortedReglements.length > 0 ? (
                <List
                    itemLayout="horizontal"
                    dataSource={sortedReglements}
                    renderItem={(item, index) => {
                        const isSansPrime = item?.sansPrime;
                        const isPaid = !isSansPrime && (item.modePaiement || item.montantPaye || item?.avenant?.code === "AN");
                        // Version du contrat liée au mouvement (repli : contrat affiché).
                        const version = versionsValidation[item?.contratCodeId] || (!item?.contratCodeId ? contrat : null);
                        const traces = [
                            { label: "Date de validation", icon: <CalendarOutlined />, value: version?.dateValidation ? frenchDate(version.dateValidation) : null },
                            { label: "Validé par", icon: <CheckCircleOutlined />, value: nomUtilisateur(version?.validateur) },
                            { label: "Encaissé par", icon: <WalletOutlined />, value: isPaid ? nomUtilisateur(item?.caissier) : null },
                            {
                                label: "Modifié par", icon: <EditOutlined />,
                                value: item?.updatedBy
                                    ? `${modificateurs[item.updatedBy] || "—"}${item?.updatedAt ? ` le ${frenchDate(item.updatedAt)}` : ""}`
                                    : null,
                            },
                        ];

                        return (
                            <Card
                                style={{
                                    ...cardStyle,
                                    background: isSansPrime ? "#f0f5ff" : (isPaid ? "#f6ffed" : "#fffbe6"),
                                    borderLeft: `4px solid ${isSansPrime ? primaryColor : (isPaid ? successColor : warningColor)}`,
                                    transition: "all 0.3s ease",
                                    cursor: "pointer"
                                }}
                                hoverable
                                bodyStyle={{ padding: "20px 24px" }}
                            >
                                <List.Item
                                    style={{ border: "none", padding: 0 }}
                                    actions={[
                                        isSansPrime ? null : (isPaid ? (
                                            <Button
                                                type="primary"
                                                icon={<ZoomInOutlined />}
                                                onClick={() => loadModal(item)}
                                                style={{
                                                    borderRadius: "8px",
                                                    height: "40px",
                                                    background: primaryColor,
                                                    borderColor: primaryColor,
                                                    fontWeight: 500,
                                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                                }}
                                            >
                                                Prévisualiser
                                            </Button>
                                        ) : (
                                            <Tag
                                                icon={<CreditCardFilled />}
                                                color="error"
                                                style={{
                                                    borderRadius: "8px",
                                                    padding: "6px 14px",
                                                    fontSize: "13px",
                                                    fontWeight: 500
                                                }}
                                            >
                                                À régler
                                            </Tag>
                                        )),
                                        // Pas de bouton « CP » sur un Duplicata (AD) : il ne modifie
                                        // pas le contrat → aucune CP propre à ce mouvement à afficher.
                                        (item?.avenant?.code !== "AD") && (
                                            <Button
                                                icon={<FileTextOutlined />}
                                                onClick={() => loadCP(item)}
                                                style={{
                                                    borderRadius: "8px",
                                                    height: "40px",
                                                    fontWeight: 500,
                                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                                }}
                                            >
                                                CP
                                            </Button>
                                        ),
                                        (!isPaid && !isSansPrime && !item?.annule && item?.avenant?.code !== "AN"
                                            && rightsService && rightsService("HCO082")) ? (
                                            <Button
                                                danger
                                                icon={<StopOutlined />}
                                                onClick={() => confirmAnnulerAvenant(item)}
                                                style={{
                                                    borderRadius: "8px",
                                                    height: "40px",
                                                    fontWeight: 500,
                                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                                }}
                                            >
                                                Annuler l'avenant
                                            </Button>
                                        ) : null
                                    ].filter(Boolean)}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <Badge
                                                count={index + 1}
                                                style={{
                                                    background: secondaryColor,
                                                    fontSize: "14px",
                                                    fontWeight: 600
                                                }}
                                            >
                                                <Avatar
                                                    size={64}
                                                    style={{
                                                        background: `linear-gradient(135deg, ${secondaryColor} 0%, ${primaryColor} 100%)`,
                                                        fontSize: "20px",
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {item?.avenant?.code || contrat?.avenant?.code || "N/A"}
                                                </Avatar>
                                            </Badge>
                                        }
                                        title={
                                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                                <Space size="middle" wrap>
                                                    <Text strong style={{ fontSize: "16px", color: secondaryColor }}>
                                                        {item?.avenant?.nom || contrat?.avenant?.nom}
                                                    </Text>
                                                    {getStatusTag(item)}
                                                    {item?.libelle?.toLowerCase().includes('reprise') && (
                                                        <Tag style={{ background: 'rgba(250,173,20,0.2)', border: '1px solid #faad14', color: '#faad14', borderRadius: '8px', padding: '4px 12px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>
                                                            📥 REPRISE
                                                        </Tag>
                                                    )}
                                                </Space>
                                            </Space>
                                        }
                                        description={
                                            <Space direction="vertical" size="middle" style={{ width: "100%", marginTop: "12px" }}>
                                                {/* Montant */}
                                                <div style={{
                                                    background: "white",
                                                    padding: "12px 16px",
                                                    borderRadius: "8px",
                                                    border: `1px solid ${secondaryColor}10`
                                                }}>
                                                    <Space size="large" wrap>
                                                        {isSansPrime ? (
                                                            <>
                                                                <div>
                                                                    <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
                                                                        Type de mouvement
                                                                    </Text>
                                                                    <Text strong style={{ fontSize: "16px", color: secondaryColor }}>
                                                                        Avenant sans prime
                                                                    </Text>
                                                                </div>
                                                                {item.createdAt && (
                                                                    <>
                                                                        <Divider type="vertical" style={{ height: "40px" }} />
                                                                        <div>
                                                                            <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
                                                                                <CalendarOutlined style={{ marginRight: "4px" }} />
                                                                                Date d'effet
                                                                            </Text>
                                                                            <Text strong style={{ fontSize: "14px", color: primaryColor }}>
                                                                                {frenchDate(item.createdAt)}
                                                                            </Text>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div>
                                                                <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
                                                                    {isPaid ? "Montant payé" : "Montant à payer"}
                                                                </Text>
                                                                <Text strong style={{ fontSize: "20px", color: isPaid ? successColor : warningColor }}>
                                                                    {displayNumbers(isPaid ? montantPayeEff(item) : item?.montant)} FCFA
                                                                </Text>
                                                            </div>
                                                        )}

                                                        {/* Reste à payer (encaissement partiel) */}
                                                        {estPartiel(item) && (
                                                            <>
                                                                <Divider type="vertical" style={{ height: "40px" }} />
                                                                <div>
                                                                    <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
                                                                        Reste à payer
                                                                    </Text>
                                                                    <Text strong style={{ fontSize: "20px", color: warningColor }}>
                                                                        {displayNumbers(resteAPayer(item))} FCFA
                                                                    </Text>
                                                                </div>
                                                            </>
                                                        )}

                                                        {/* Mode de paiement */}
                                                        {item.modePaiement && (
                                                            <Divider type="vertical" style={{ height: "40px" }} />
                                                        )}
                                                        {item.modePaiement && (
                                                            <div>
                                                                <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
                                                                    Mode de paiement
                                                                </Text>
                                                                <Space>
                                                                    {getPaymentIcon(item.modePaiement)}
                                                                    <Text strong style={{ fontSize: "14px" }}>
                                                                        {getPaymentMethodLabel(item.modePaiement)}
                                                                    </Text>
                                                                </Space>
                                                            </div>
                                                        )}

                                                        {/* Date de paiement */}
                                                        {item.dateReglement && (
                                                            <>
                                                                <Divider type="vertical" style={{ height: "40px" }} />
                                                                <div>
                                                                    <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
                                                                        <CalendarOutlined style={{ marginRight: "4px" }} />
                                                                        Date de paiement
                                                                    </Text>
                                                                    <Text strong style={{ fontSize: "14px", color: primaryColor }}>
                                                                        {frenchDate(item.dateReglement)}
                                                                    </Text>
                                                                </div>
                                                            </>
                                                        )}

                                                        {/* Observation / commentaire saisi à l'encaissement */}
                                                        {item.observation && (
                                                            <>
                                                                <Divider type="vertical" style={{ height: "40px" }} />
                                                                <div style={{ maxWidth: "260px" }}>
                                                                    <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
                                                                        Observation
                                                                    </Text>
                                                                    <Text strong style={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>
                                                                        {item.observation}
                                                                    </Text>
                                                                </div>
                                                            </>
                                                        )}
                                                    </Space>
                                                </div>

                                                {/* Traçabilité */}
                                                <div style={{
                                                    background: "white",
                                                    padding: "10px 16px",
                                                    borderRadius: "8px",
                                                    border: `1px dashed ${secondaryColor}30`
                                                }}>
                                                    <Space size="large" wrap>
                                                        {traces.map((t) => (
                                                            <div key={t.label}>
                                                                <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
                                                                    <span style={{ marginRight: "4px" }}>{t.icon}</span>
                                                                    {t.label}
                                                                </Text>
                                                                <Text strong={!!t.value} type={t.value ? undefined : "secondary"} style={{ fontSize: "13px" }}>
                                                                    {t.value || "—"}
                                                                </Text>
                                                            </div>
                                                        ))}
                                                    </Space>
                                                </div>

                                                {/* Tag versement pour courtier */}
                                                {contrat?.courtier && (
                                                    <div>
                                                        {getVersementTag(item?.versementCompagnie)}
                                                    </div>
                                                )}
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            </Card>
                        );
                    }}
                />
            ) : (
                <Card style={cardStyle}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <Space direction="vertical" size="small">
                                <Text style={{ fontSize: "16px", color: "#999" }}>
                                    Aucun mouvement effectué sur ce contrat
                                </Text>
                                <Text type="secondary" style={{ fontSize: "14px" }}>
                                    Les paiements et avenants apparaîtront ici
                                </Text>
                            </Space>
                        }
                        style={{ padding: "60px 20px" }}
                    />
                </Card>
            )}
            <Modal
                title="Reçu de Paiement"
                open={openRecu}
                onCancel={() => setOpenRecu(false)}
                footer={null}
                width={1200}
                height={2000}
            >
                {selectedReglement ? (
                    <RecuCurrentFormat ref={printRecu} contrat={contrat} reglement={selectedReglement} />
                ) : (
                    <Text>Aucun reçu disponible.</Text>
                )}
                <Button
                    type="primary"
                    style={{ marginTop: "20px", borderRadius: "8px", height: "40px", fontWeight: 500 }}
                    onClick={() => {
                        handleRecu();
                    }}
                >
                    Imprimer le Reçu
                </Button>
            </Modal>
            <Modal
                title="Conditions Particulières (CP)"
                open={openCP}
                onCancel={() => setOpenCP(false)}
                footer={null}
                width={1200}
                height={2000}
            >
                <Spin spinning={loadingCP} tip="Chargement du CP...">
                    <div style={{ display: "flex", justifyContent: "center", background: "#fafafa", padding: "24px", borderRadius: "8px" }}>
                        <div style={{ width: "210mm", height: "auto" }}>
                            {selectedCPContrat ? (
                                renderCPDocument(selectedCPContrat, printCP)
                            ) : (
                                !loadingCP && <Text>Aucun CP disponible.</Text>
                            )}
                        </div>
                    </div>
                </Spin>
                <Button
                    type="primary"
                    disabled={!selectedCPContrat || loadingCP}
                    style={{ marginTop: "20px", borderRadius: "8px", height: "40px", fontWeight: 500 }}
                    onClick={() => {
                        handleCP();
                    }}
                >
                    Imprimer le CP
                </Button>
            </Modal>
        </div>
    );
};

export default MouvementsContent;